import { createSelector } from 'reselect';
import { getThemes } from 'selectors/themesSelectors';
import { State } from 'reducers';
import { isDMGContainerType, isPIContainerType } from 'reducers/editorReducer';
import { values } from 'lodash';
import { ThemeStatus, Container } from 'shared/models';
import { isEqual } from 'lodash';

const getEditorState =
    (state: State) =>
        state.editor;

export const getEditorExitPath =
    createSelector(getEditorState,
        state => state.exitPath
    );

export const getEditorCurrentState =
    createSelector(getEditorState,
        state => state.current
    );

export const getEditorBuildingId =
    createSelector(getEditorState,
        state => state.buildingId
    );

export const getEditorOriginal =
    createSelector(getEditorState,
        state => state.original
    );

export const getEditorTheme =
    createSelector(getEditorCurrentState,
        state => state.theme
    );

export const getEditorThemeAssets =
    createSelector(getEditorTheme,
        theme => theme ? theme.assets : null
    );

const getId =
    (state: State, { id }: { id: string }) => id;

export const getEditorThemeAsset =
    createSelector(
        getEditorThemeAssets,
        getId,
        (assets, id) => (assets && assets[id]) || null
    );

export const getEditorThemeContainers =
    createSelector(getEditorTheme,
        theme => theme ? theme.containers : {}
    );

export const getEditorThemeIsEcs =
    createSelector(
        getEditorThemeContainers,
        (containers: Record<string, Container>) => containers && values(containers).some(c => c.type === 'ecs')
    );

export const getContainerOrder =
    createSelector(getEditorTheme,
        theme => theme ? theme.containerOrder : []
    );

// True if the Publishing status screen is visible... doesn't actually indicate if a given theme is being published
export const isEditorPublishPending =
    createSelector(getEditorState,
        state => state.publishPending
    );

export const getEditorThemeId =
    createSelector(getEditorTheme,
        theme => theme && theme.id
    );

// Status of a theme will be updated in the themes state, not the editor state!
export const getThemeStatus =
    createSelector(
        getThemes,
        getEditorThemeId,
        (themes, id) => {
            if (!themes || !id) return null;
            const theme = themes[id];
            if (!theme) return ThemeStatus.Idle;
            return theme.status;
        }
    );

export const getEditorThemeName =
    createSelector(getEditorTheme,
        theme => theme && theme.displayName
    );

export const getActiveContainerId =
    createSelector(getEditorCurrentState,
        state => state.activeContainerId
    );

export const getActiveContainer =
    createSelector(
        getEditorThemeContainers,
        getActiveContainerId,
        (containers, id) => containers && id && containers[id]
    );

export const isActiveContainerLocked =
    createSelector(
        getActiveContainer,
        container => !!(container && container.locked)
    );

export const isActiveContainerFloorArrival =
    createSelector(
        getActiveContainer,
        container => !!(container && container.type === 'floorArrival')
    );

export const isActiveContainerPI =
    createSelector(
        getActiveContainer,
        container => {
            if (!container) return false;
            const type = container.type || 'standard';
            return isPIContainerType(type);
        }
    );

export const isActiveContainerDMG =
    createSelector(
        getActiveContainer,
        container => {
            if (!container) return false;
            const type = container.type || 'standard';
            return isDMGContainerType(type);
        }
    );

export const getActiveContainerAssetOrder =
    createSelector(getActiveContainer,
        container => (container && container.assetOrder) || []
    );

export const getActiveContainerType =
    createSelector(getActiveContainer,
        container => container ? (container.type || 'standard') : null
    );

export const getActiveAssetId =
    createSelector(getEditorCurrentState,
        state => state.activeAssetId
    );

export const getActiveAsset =
    createSelector(
        getEditorThemeAssets,
        getActiveAssetId,
        (assets, id) => assets && id ? assets[id] : null
    );

const getAssetId =
    (state: State, { assetId }: { assetId: string }) => assetId;

export const getAssetUpload =
    createSelector(
        getEditorState,
        getAssetId,
        (state, assetId) => state.upload[assetId] || null
    );

export const getAssetUploadProgress =
    createSelector(getAssetUpload,
        state => state ? state.progress : 0
    );

export const getAssetUploadError =
    createSelector(getAssetUpload,
        state => state ? state.error : null
    );


const getPrefix = (state: State, { prefix }: { prefix: string }) => prefix;
export const getNextUniqueContainerName =
    createSelector(
        getEditorThemeContainers,
        getPrefix,
        (containers, prefix) => {
            for (let i = 1; ; i++) {
                const name = `${prefix}${i}`;
                if (!containers || !values(containers).some(c => c.displayName === name)) {
                    return name;
                }
            }
        }
    );

export const getActiveRegion =
    createSelector(getEditorState,
        state => state.activeRegion
    );

export const getPreviewContainerRegion =
    createSelector(getEditorState,
        state => state.previewRegion
    );

export const isEditorDirty =
    createSelector(
        getEditorTheme,
        getEditorOriginal,
        (theme, original) => !isEqual(theme, original)
    );

export const isEditorSavePending =
    createSelector(getEditorState,
        state => state.savePending
    );

export const getError =
    createSelector(getEditorState,
        state => state.error
    );

export const isEditorErrorVisible =
    createSelector(getEditorState,
        state => state.errorVisible
    );

export const getZoomLevel =
    createSelector(getEditorState,
        state => state.zoom || 1
    );

export const getFitZoom =
    createSelector(getEditorState,
        state => state.fitZoom
    );

export const getUndoState =
    createSelector(getEditorState,
        state => state.undo
    );

export const getRedoState =
    createSelector(getEditorState,
        state => state.redo
    );

export const getCanUndo =
    createSelector(getUndoState,
        state => state.length > 0
    );

export const getCanRedo =
    createSelector(getRedoState,
        state => state.length > 0
    );

export const getLastAction =
    createSelector(getEditorState,
        state => state.lastAction
    );

const getNewsState =
    createSelector(getEditorState,
        state => state.news
    );

const getChannelId =
    (state: State, { channelId }: { channelId: string }) => channelId;

export const getNewsChannelState =
    createSelector(
        getNewsState,
        getChannelId,
        (state, channelId) => (channelId && state[channelId]) || null
    );

export const getNews =
    createSelector(getNewsChannelState,
        state => state && state.items
    );

export const isNewsLoading =
    createSelector(getNewsChannelState,
        state => state ? state.loading : false
    );

export const getNewsLastUpdated =
    createSelector(getNewsChannelState,
        state => state ? state.updated : 0
    );

export const getNewsError =
    createSelector(getNewsChannelState,
        state => state ? state.error : null
    );

const getUrl =
    (state: State, { url }: { url: string }) => url;

const getProvider =
    (state: State, { provider }: { provider: string | null }) => provider;

const getLocationOrLatLong =
    (state: State, { locationOrLatLong }: { locationOrLatLong: string | null }) => locationOrLatLong;

const getWeatherState =
    createSelector(getEditorState,
        state => state.weather
    );

const getWeatherForLocation =
    createSelector(
        getWeatherState,
        getLocationOrLatLong,
        getProvider,
        (state, locationOrLatLong, provider) => {
            if (!state) return null;
            return state[`${provider}:${locationOrLatLong}`] || null;
        }
    );

export const getWeather =
    createSelector(
        getWeatherForLocation,
        state => state ? state.items : null
    );

export const isWeatherLoading =
    createSelector(
        getWeatherForLocation,
        state => state ? state.loading : false
    );

export const getWeatherLastUpdated =
    createSelector(
        getWeatherForLocation,
        state => state ? state.updated : 0
    );

export const getWeatherError =
    createSelector(
        getWeatherForLocation,
        state => (state && state.error) || null
    );

const getStockSymbols =
    (state: State, { symbols }: { symbols: string }) => symbols;

const getStockProvider  =
    (state: State, { provider }: { provider: string }) => provider || 'default';

const getStocksState =
    createSelector(getEditorState,
        state => state.stocks
    );

const getStocksForSymbols =
    createSelector(
        getStocksState,
        getStockSymbols,
        getStockProvider,
        (state, symbols, provider) => {
            if (!state || !symbols || !provider) return null;
            const providerStocks = state[provider];
            if (!providerStocks) return null;
            return providerStocks[symbols] || null;
        }
    );

export const getStocks =
    createSelector(
        getStocksForSymbols,
        state => state ? state.items : []
    );

export const isLoadingStocks =
    createSelector(
        getStocksForSymbols,
        state => state ? state.loading : false
    );

export const getLoadingStocksError =
    createSelector(
        getStocksForSymbols,
        state => state ? state.error : null
    );

export const getStocksLastUpdated =
    createSelector(
        getStocksForSymbols,
        state => state ? state.updated : 0
    );

export const isLoadingFonts =
    createSelector(getEditorState,
        state => state.fonts.loading
    );

export const getFonts =
    createSelector(getEditorState,
        state => state.fonts.data
    );

export const getCustomFonts =
    createSelector(getEditorState,
        state => state.customFonts
    );